import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Download,
  ChevronRight,
  ArrowUpRight,
  Ship,
  Container as ContainerIcon,
  Columns3,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  X,
} from 'lucide-react';

import {
  etaLabel,
  fmtDate,
  type Company,
} from '@/features/dashboard/data/claudeMockData';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import {
  ALL_CONTAINER_STATUSES,
  containerStatusLabel,
  statusFilterOptions,
} from '../constants/statuses';
import { formatEtaHeading, formatEtaSubtext } from '../constants/directionLabels';
import { RouteCell, CompanyBadge } from '@/features/dashboard/components/claude/shared';
import type { ContainerRow } from '../interfaces/container.interface';
import { useContainers } from '../hooks/useContainers';
import { ContainerViewModal } from '../components/ContainerViewModal';
import { ClientNoteEditor } from '@/components/claude/ClientNoteEditor';
import { useSaveClientNote } from '@/hooks/useClientNote';
import { exportXlsx, type ExcelColumn } from '@/utils/exportXlsx';
import { formatQuotedOceanFreight, hasQuotedOceanFreight } from '@/utils/oceanFreight';
import {
  type ContainerColumnId,
  type ContainerColumnState,
  type ContainersSortDir,
  type ContainersSortKey,
  columnLabel,
  defaultContainerColumns,
  loadContainersPrefs,
  moveColumn,
  saveContainersPrefs,
  toggleColumnVisible,
} from '../utils/containersTablePrefs';

/**
 * Construiește un Company sintetic pentru CompanyBadge folosind datele venite
 * din backend (companyName/companyId). Aceeași strategie ca în dashboard.
 */
function buildCompanyForBadge(name: string, id: number): Company | undefined {
  if (!name) return undefined;
  const palette = ['#1e6bd6', '#0c8a5b', '#b6500f', '#7a3aa8', '#0f7a8a', '#c0392b'];
  const color = palette[id % palette.length] || palette[0];
  const short =
    name
      .split(/\s+/)
      .map((p) => p.charAt(0).toUpperCase())
      .join('')
      .slice(0, 3) || name.slice(0, 3).toUpperCase();
  return { id: `co_${id}`, name, short, color, vat: '' };
}

interface SortHeaderProps {
  id: ContainersSortKey;
  label: string;
  sortKey: ContainersSortKey;
  sortDir: ContainersSortDir;
  onSort: (k: ContainersSortKey) => void;
  align?: 'right';
}

const SortHeader: React.FC<SortHeaderProps> = ({ id, label, sortKey, sortDir, onSort, align }) => {
  const isSorted = sortKey === id;
  return (
    <th
      className={'sortable' + (isSorted ? ' sorted' : '')}
      onClick={() => onSort(id)}
      style={align === 'right' ? { textAlign: 'right' } : undefined}
    >
      {label}
      <span className="sort-ind">{isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

const CONTAINER_EXCEL_WIDTH: Record<ContainerColumnId, number> = {
  no: 16,
  company: 26,
  shipper: 28,
  booking: 14,
  route: 16,
  vessel: 24,
  onBoard: 14,
  eta: 14,
  oceanFreight: 18,
  status: 18,
  note: 28,
};

/**
 * Valoare Excel per coloană a tabelului. Tipul și marfa (afișate sub numărul
 * containerului în UI) sunt coloane separate în Excel — vezi `containerExtraExcelColumns`.
 */
function containerExcelValue(
  id: ContainerColumnId,
  r: ContainerRow,
  lang: 'ro' | 'en',
): string | number | Date | null {
  switch (id) {
    case 'no':
      return r.containerNumber || '';
    case 'company':
      return r.companyName || '';
    case 'shipper':
      return r.shipperName || '';
    case 'booking':
      return r.bookingNumber || '';
    case 'route':
      return r.pol || r.pod ? `${r.pol || '—'} → ${r.pod || '—'}` : '';
    case 'vessel':
      return [r.vesselName, r.vesselCode].filter(Boolean).join(' · ');
    case 'onBoard':
      return r.onBoardDate ? new Date(r.onBoardDate) : null;
    case 'eta':
      return r.eta ? new Date(r.eta) : null;
    case 'oceanFreight':
      return formatQuotedOceanFreight(r, lang);
    case 'status':
      return containerStatusLabel(r.status, lang, r.rawStatus || r.status);
    case 'note':
      return r.clientNote || '';
    default:
      return '';
  }
}

/**
 * Coloane suplimentare în Excel, care în tabel apar doar ca text secundar
 * (sub numărul containerului) sau deloc: tip, marfă, B/L-uri, greutate, ETA port.
 */
function containerExtraExcelColumns(lang: 'ro' | 'en'): ExcelColumn<ContainerRow>[] {
  const isRo = lang === 'ro';
  return [
    { header: isRo ? 'Tip' : 'Type', value: (r) => r.containerType || '', width: 8 },
    { header: isRo ? 'Marfă' : 'Commodity', value: (r) => r.commodity || '', width: 32 },
    {
      header: isRo ? 'Marfă (tradusă)' : 'Commodity (translated)',
      value: (r) => r.translatedCommodity || '',
      width: 32,
    },
    { header: 'B/L', value: (r) => (r.bls || []).join(', '), width: 22 },
    {
      header: isRo ? 'Greutate brută (kg)' : 'Gross weight (kg)',
      value: (r) => (r.grossWeight != null ? r.grossWeight : null),
      width: 18,
    },
    { header: 'POL', value: (r) => r.pol || '', width: 8 },
    { header: 'POD', value: (r) => r.pod || '', width: 8 },
    {
      header: isRo ? 'Port ETA' : 'ETA port',
      value: (r) => [r.etaPort, r.etaPortName].filter(Boolean).join(' · '),
      width: 22,
    },
  ];
}

const truncateCommodity = (s: string | null | undefined, max = 30): string => {
  if (!s) return '';
  return s.length > max ? s.slice(0, max) + '...' : s;
};

const sortValue = (row: ContainerRow, key: ContainersSortKey): string | number => {
  switch (key) {
    case 'no':
      return (row.containerNumber || '').toLowerCase();
    case 'company':
      return (row.companyName || '').toLowerCase();
    case 'shipper':
      return (row.shipperName || '').toLowerCase();
    case 'booking':
      return (row.bookingNumber || '').toLowerCase();
    case 'route':
      return `${row.pol || ''}|${row.pod || ''}`.toLowerCase();
    case 'vessel':
      return (row.vesselName || '').toLowerCase();
    case 'onBoard':
      return row.onBoardDate
        ? new Date(row.onBoardDate).getTime()
        : Number.POSITIVE_INFINITY;
    case 'eta':
      return row.eta ? new Date(row.eta).getTime() : Number.POSITIVE_INFINITY;
    case 'oceanFreight':
      return hasQuotedOceanFreight(row)
        ? (row.oceanFreightAmount as number)
        : Number.POSITIVE_INFINITY;
    case 'status':
      return row.status;
    case 'note':
      return (row.clientNote || '').toLowerCase();
    default:
      return '';
  }
};

/** Coloane sortabile din headere (restul sunt doar afișare). */
const SORTABLE: Set<ContainerColumnId> = new Set([
  'no',
  'company',
  'shipper',
  'booking',
  'route',
  'vessel',
  'onBoard',
  'eta',
  'oceanFreight',
  'status',
  'note',
]);

export const ContainersIndex: React.FC = () => {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';
  const dirKey = direction === 'export' ? 'export' : 'import';

  const { data, isLoading } = useContainers();
  const { saveContainer } = useSaveClientNote();
  const rows: ContainerRow[] = data || [];

  // Preferințe per direcție — încărcate la mount / schimbare direcție.
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [sortKey, setSortKey] = useState<ContainersSortKey>('eta');
  const [sortDir, setSortDir] = useState<ContainersSortDir>('asc');
  const [selected, setSelected] = useState<ContainerRow | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [columns, setColumns] = useState<ContainerColumnState[]>(() =>
    defaultContainerColumns(dirKey),
  );
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const colMenuRef = useRef<HTMLDivElement>(null);
  const prefsHydrated = useRef(false);
  const skipPageReset = useRef(true);

  // Aplică background-ul claude-design pe `.content` cât timp pagina e montată.
  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  // Reîncarcă preferințele când se schimbă direcția (import/export separat).
  useEffect(() => {
    const p = loadContainersPrefs(dirKey);
    setStatusFilter(p.statusFilter);
    setSearch(p.search);
    setSortKey(p.sortKey);
    setSortDir(p.sortDir);
    setPage(p.page);
    setPageSize(p.pageSize);
    setColumns(p.columns);
    prefsHydrated.current = true;
    skipPageReset.current = true;
    setColMenuOpen(false);
  }, [dirKey]);

  // Salvează preferințele (după hidratare) — pe direcția curentă.
  useEffect(() => {
    if (!prefsHydrated.current) return;
    saveContainersPrefs(dirKey, {
      statusFilter,
      search,
      sortKey,
      sortDir,
      page,
      pageSize,
      columns,
    });
  }, [dirKey, statusFilter, search, sortKey, sortDir, page, pageSize, columns]);

  // Închide meniul coloane la click în afară / Escape.
  useEffect(() => {
    if (!colMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) {
        setColMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [colMenuOpen]);

  // Deschide modalul automat când vine pe URL `?focus=<id>` (ex. din command palette).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || rows.length === 0) return;
    const id = Number(focus);
    if (!Number.isFinite(id)) return;
    const target = rows.find((r) => r.id === id);
    if (target) {
      setSelected(target);
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rows]);

  const onSort = (k: ContainersSortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // Date (eta, onBoard): ASC implicit; text: ASC.
      setSortDir(k === 'onBoard' ? 'desc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    let arr = rows;

    if (statusFilter !== 'all') {
      arr = arr.filter((r) => r.status === statusFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) =>
        [
          r.containerNumber,
          r.bookingNumber,
          r.vesselName,
          r.vesselCode,
          r.commodity,
          r.translatedCommodity,
          r.companyName,
          r.shipperName,
          r.pol,
          r.pod,
          r.clientNote,
          ...r.bls,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }

    return [...arr].sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      // Null dates last regardless of direction.
      if (typeof va === 'number' && typeof vb === 'number') {
        if (va === Number.POSITIVE_INFINITY && vb === Number.POSITIVE_INFINITY) return 0;
        if (va === Number.POSITIVE_INFINITY) return 1;
        if (vb === Number.POSITIVE_INFINITY) return -1;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, statusFilter, search, sortKey, sortDir]);

  // Reset pagină la 1 când se schimbă filtre/sort (nu la hidratare din storage).
  useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false;
      return;
    }
    setPage(1);
  }, [statusFilter, search, sortKey, sortDir, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Dacă pagina salvată e peste total, o aducem în range (fără a reseta pe 1).
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paginated = filtered.slice(pageStart, pageStart + pageSize);
  const showingFrom = filtered.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(filtered.length, pageStart + pageSize);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible),
    [columns],
  );
  const colSpan = visibleColumns.length + 1; // + actions

  const etaHeading = isExport ? formatEtaHeading(direction, lang) : 'ETA';

  // Export Excel: TOATE coloanele tabelului (și cele ascunse), în ordinea
  // configurată, plus coloanele suplimentare (tip, marfă, B/L-uri, greutate…).
  // Rândurile = ce e filtrat/căutat curent, toate paginile.
  const handleExport = () => {
    if (filtered.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const tableColumns: ExcelColumn<ContainerRow>[] = columns.map((col) => ({
      header: columnLabel(col.id, lang, etaHeading),
      value: (r) => containerExcelValue(col.id, r, lang),
      width: CONTAINER_EXCEL_WIDTH[col.id],
    }));
    exportXlsx<ContainerRow>({
      fileName: `containere-${isExport ? 'export' : 'import'}-${today}`,
      sheetName: isRo ? 'Containere' : 'Containers',
      rows: filtered,
      columns: [...tableColumns, ...containerExtraExcelColumns(lang)],
    });
  };

  const statusOptions = statusFilterOptions(direction);

  useEffect(() => {
    if (statusFilter === 'all') return;
    if (statusOptions.some((o) => o.id === statusFilter)) return;
    setStatusFilter('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const resetColumns = useCallback(() => {
    setColumns(defaultContainerColumns(dirKey));
  }, [dirKey]);

  const renderHeaderCell = (col: ContainerColumnState) => {
    const label = columnLabel(col.id, lang, etaHeading);
    if (SORTABLE.has(col.id)) {
      return (
        <SortHeader
          key={col.id}
          id={col.id as ContainersSortKey}
          label={label}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          align={col.id === 'oceanFreight' ? 'right' : undefined}
        />
      );
    }
    return (
      <th key={col.id}>
        {label}
      </th>
    );
  };

  const renderBodyCell = (col: ContainerColumnState, c: ContainerRow) => {
    const st = ALL_CONTAINER_STATUSES[c.status];
    const co = buildCompanyForBadge(c.companyName, c.companyId);
    switch (col.id) {
      case 'no':
        return (
          <td key={col.id}>
            <div className="mono fw-600 fs-13">
              <span className="ctn-no">
                {c.containerNumber || '—'}
                <ArrowUpRight className="ctn-no-arrow" size={13} strokeWidth={2.4} />
              </span>
            </div>
            <div className="muted fs-11 mt-1">
              {c.containerType}
              {c.commodity ? ` · ${truncateCommodity(c.commodity)}` : ''}
            </div>
          </td>
        );
      case 'company':
        return (
          <td key={col.id}>
            <CompanyBadge company={co} />
          </td>
        );
      case 'shipper':
        return (
          <td key={col.id}>
            <span
              className="fs-13"
              title={c.shipperName || ''}
              style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}
            >
              {c.shipperName || '—'}
            </span>
          </td>
        );
      case 'booking':
        return (
          <td key={col.id}>
            <span className="mono fs-12 muted">{c.bookingNumber || '—'}</span>
          </td>
        );
      case 'route':
        return (
          <td key={col.id}>
            {c.pol && c.pod ? (
              <RouteCell pol={c.pol} pod={c.pod} />
            ) : (
              <span className="muted fs-12">—</span>
            )}
          </td>
        );
      case 'vessel':
        return (
          <td key={col.id}>
            <div className="fw-500 fs-13" style={{ whiteSpace: 'nowrap' }}>
              {c.vesselName || '—'}
            </div>
            <div className="muted fs-11 mt-1 mono">{c.vesselCode || ''}</div>
          </td>
        );
      case 'onBoard':
        return (
          <td key={col.id} className="fs-13 muted" style={{ whiteSpace: 'nowrap' }}>
            {c.onBoardDate ? fmtDate(c.onBoardDate, lang) : '—'}
          </td>
        );
      case 'eta':
        return (
          <td key={col.id} style={{ minWidth: 132, whiteSpace: 'nowrap' }}>
            <div className="fw-500 fs-13">{c.eta ? fmtDate(c.eta, lang) : '—'}</div>
            <div className="muted fs-11">
              {c.eta
                ? isExport
                  ? formatEtaSubtext(direction, lang, c) ||
                    formatEtaHeading(direction, lang, c)
                  : etaLabel(c.eta, lang)
                : ''}
            </div>
          </td>
        );
      case 'oceanFreight': {
        const text = formatQuotedOceanFreight(c, lang);
        return (
          <td key={col.id} style={{ textAlign: 'right' }}>
            {text === '—' ? (
              <span className="muted">—</span>
            ) : (
              <span className="mono fw-600 fs-13">{text}</span>
            )}
          </td>
        );
      }
      case 'status':
        return (
          <td key={col.id}>
            {st ? (
              <span className={'badge ' + st.badge}>
                <span className="dot" />
                {containerStatusLabel(c.status, lang)}
              </span>
            ) : (
              <span className="muted fs-11">{c.rawStatus || c.status}</span>
            )}
          </td>
        );
      case 'note':
        return (
          <td
            key={col.id}
            className="client-note-cell"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ClientNoteEditor
              value={c.clientNote}
              onSave={(next) => saveContainer(c.id, next).then(() => undefined)}
              variant="cell"
              lang={lang}
            />
          </td>
        );
      default:
        return <td key={col.id}>—</td>;
    }
  };

  const renderEmpty = (span: number) => (
    <tr>
      <td colSpan={span}>
        <div className="empty">
          <div className="icon">
            <ContainerIcon size={22} strokeWidth={2} />
          </div>
          <div className="fw-600">
            {isLoading
              ? isRo
                ? 'Se încarcă…'
                : 'Loading…'
              : isRo
                ? 'Niciun container'
                : 'No containers'}
          </div>
          {!isLoading && (
            <div className="muted fs-12 mt-1">
              {isRo
                ? 'Încercați să schimbați filtrele sau căutarea.'
                : 'Try changing filters or search.'}
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div style={{ color: 'var(--text)' }}>
      <div className="ctn-page-head">
        <h1>
          {isRo
            ? isExport
              ? 'Containere export'
              : 'Containere'
            : isExport
              ? 'Export containers'
              : 'Containers'}
          <span className="ctn-count">
            {filtered.length} {isRo ? 'rezultate' : 'results'}
          </span>
        </h1>
        <div className="muted fs-13 mt-1 hidden-mobile">
          {isRo
            ? isExport
              ? 'Toate containerele tale de export, indiferent de status'
              : 'Toate containerele tale, indiferent de status'
            : isExport
              ? 'All your export containers, across statuses'
              : 'All your containers, across statuses'}
        </div>
      </div>

      <div className="ctn-toolbar">
        <div className="ctn-tb-row">
          <div className="search-input ctn-search">
            <Search size={15} />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder={isRo ? 'Container, BL, booking, navă…' : 'Container, BL, booking, vessel…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={isRo ? 'Caută containere' : 'Search containers'}
            />
            {search && (
              <button
                type="button"
                className="ctn-search-clear"
                onClick={() => setSearch('')}
                aria-label={isRo ? 'Șterge căutarea' : 'Clear search'}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="ctn-tb-actions">
        <div className="col-config-wrap hidden-mobile" ref={colMenuRef}>
          <button
            type="button"
            className={'btn btn-sm' + (colMenuOpen ? ' active' : '')}
            onClick={() => setColMenuOpen((o) => !o)}
            title={isRo ? 'Configurează coloanele' : 'Configure columns'}
            aria-expanded={colMenuOpen}
          >
            <Columns3 size={13} />
            {isRo ? 'Coloane' : 'Columns'}
          </button>
          {colMenuOpen && (
            <div className="dropdown col-config-menu" role="menu">
              <div className="col-config-title">
                {isRo
                  ? `Coloane (${isExport ? 'export' : 'import'})`
                  : `Columns (${isExport ? 'export' : 'import'})`}
              </div>
              {columns.map((col, idx) => (
                <div key={col.id} className="col-config-row">
                  <label>
                    <input
                      type="checkbox"
                      checked={col.visible}
                      onChange={() => setColumns((prev) => toggleColumnVisible(prev, col.id))}
                    />
                    <span className="col-name">{columnLabel(col.id, lang, etaHeading)}</span>
                  </label>
                  <div className="col-config-move">
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={() => setColumns((prev) => moveColumn(prev, idx, -1))}
                      aria-label={isRo ? 'Mută în sus' : 'Move up'}
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      disabled={idx === columns.length - 1}
                      onClick={() => setColumns((prev) => moveColumn(prev, idx, 1))}
                      aria-label={isRo ? 'Mută în jos' : 'Move down'}
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <div className="col-config-footer">
                <button type="button" className="btn btn-sm" onClick={resetColumns}>
                  <RotateCcw size={12} />
                  {isRo ? 'Reset' : 'Reset'}
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm ctn-export"
          onClick={handleExport}
          disabled={filtered.length === 0}
          aria-label={isRo ? 'Exportă în Excel' : 'Export to Excel'}
          title={
            filtered.length === 0
              ? isRo
                ? 'Nimic de exportat'
                : 'Nothing to export'
              : isRo
                ? 'Exportă lista în Excel'
                : 'Export the list to Excel'
          }
        >
          <Download size={14} />
          <span className="ctn-export-label">Export</span>
        </button>
          </div>
        </div>

        <div className="ctn-chips" role="tablist" aria-label={isRo ? 'Filtrează după status' : 'Filter by status'}>
          {statusOptions.map((s) => {
            const count =
              s.id === 'all' ? rows.length : rows.filter((r) => r.status === s.id).length;
            const active = statusFilter === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={'ctn-chip' + (active ? ' active' : '') + (count === 0 ? ' is-empty' : '')}
                onClick={() => setStatusFilter(s.id)}
              >
                {isRo ? s.labelRo : s.labelEn}
                <span className="ctn-chip-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop table */}
      <div className="card hidden-mobile">
        <div
          className="table-wrap sticky-thead"
          style={{ maxHeight: 'calc(100vh - 290px)', overflowY: 'auto' }}
        >
          <table className="data compact ctn-table">
            <thead>
              <tr>
                {visibleColumns.map(renderHeaderCell)}
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && renderEmpty(colSpan)}
              {paginated.map((c) => (
                <tr
                  key={`${c.bookingId}-${c.id}-${c.containerNumber}`}
                  className="clickable"
                  onClick={() => setSelected(c)}
                >
                  {visibleColumns.map((col) => renderBodyCell(col, c))}
                  <td>
                    <ChevronRight size={16} strokeWidth={2} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="show-mobile">
        {filtered.length === 0 && (
          <div className="card" style={{ padding: 0 }}>
            <div className="empty">
              <div className="icon">
                <ContainerIcon size={22} strokeWidth={2} />
              </div>
              <div className="fw-600">
                {isLoading
                  ? isRo
                    ? 'Se încarcă…'
                    : 'Loading…'
                  : isRo
                    ? 'Niciun container'
                    : 'No containers'}
              </div>
            </div>
          </div>
        )}
        {paginated.map((c) => {
          const st = ALL_CONTAINER_STATUSES[c.status];
          const co = buildCompanyForBadge(c.companyName, c.companyId);
          return (
            <div
              key={`m-${c.bookingId}-${c.id}-${c.containerNumber}`}
              className="card ctn-mcard"
              role="button"
              tabIndex={0}
              onClick={() => setSelected(c)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelected(c);
                }
              }}
            >
              <div className="mc-head">
                <div style={{ minWidth: 0 }}>
                  <div className="mc-no mono">
                    {c.containerNumber || '—'}
                    <ArrowUpRight size={14} strokeWidth={2.4} />
                  </div>
                  <div className="mc-desc">
                    <span className="mc-type">{c.containerType}</span>
                    {c.commodity ? <span>{c.commodity}</span> : null}
                  </div>
                </div>
                {st ? (
                  <span className={'badge ' + st.badge}>
                    <span className="dot" />
                    {containerStatusLabel(c.status, lang)}
                  </span>
                ) : (
                  <span className="muted fs-11">{c.rawStatus || c.status}</span>
                )}
              </div>

              <div className="mc-route">
                <span className="mc-port mono">{c.pol || '—'}</span>
                <span
                  className="mc-line"
                  style={{ '--mc-progress': `${Math.round(Math.max(0, Math.min(1, c.progress || 0)) * 100)}%` } as React.CSSProperties}
                >
                  <span className="mc-ship">
                    <Ship size={12} strokeWidth={2.2} />
                  </span>
                </span>
                <span className="mc-port mono">{c.pod || '—'}</span>
                <span className="mc-vessel">
                  {c.vesselName || (isRo ? 'Navă neanunțată' : 'Vessel not announced')}
                  {c.vesselCode && <span className="mono">{c.vesselCode}</span>}
                </span>
              </div>

              <div className="mc-facts">
                <div className="mc-fact">
                  <span className="k">{isExport ? formatEtaHeading(direction, lang, c) : 'ETA'}</span>
                  <span className="v">{c.eta ? fmtDate(c.eta, lang) : '—'}</span>
                  {c.eta && (
                    <span className={'s' + (new Date(c.eta).getTime() < Date.now() - 86_400_000 ? ' past' : '')}>
                      {isExport ? formatEtaSubtext(direction, lang, c) || '' : etaLabel(c.eta, lang)}
                    </span>
                  )}
                </div>
                <div className="mc-fact">
                  <span className="k">On Board</span>
                  <span className="v">{c.onBoardDate ? fmtDate(c.onBoardDate, lang) : '—'}</span>
                </div>
                {isExport ? (
                  <div className="mc-fact">
                    <span className="k">Booking</span>
                    <span className="v mono">{c.bookingNumber || '—'}</span>
                  </div>
                ) : (
                  <div className="mc-fact">
                    <span className="k">{isRo ? 'Navlu' : 'Freight'}</span>
                    <span className={'v' + (hasQuotedOceanFreight(c) ? ' mono' : ' muted')}>
                      {formatQuotedOceanFreight(c, lang)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mc-foot">
                <CompanyBadge company={co} />
                {!isExport && c.bookingNumber && <span className="mc-booking mono">{c.bookingNumber}</span>}
                {c.shipperName ? (
                  <span className="mc-shipper" title={c.shipperName}>
                    {c.shipperName}
                  </span>
                ) : null}
              </div>

              <div
                className="mc-note"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <ClientNoteEditor
                  value={c.clientNote}
                  onSave={(next) => saveContainer(c.id, next).then(() => undefined)}
                  variant="cell"
                  lang={lang}
                />
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length > 0 && (
        <div
          className="row gap-2"
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 14,
            flexWrap: 'wrap',
          }}
        >
          <div className="muted fs-12">
            {isRo
              ? `Afișare ${showingFrom}–${showingTo} din ${filtered.length}`
              : `Showing ${showingFrom}–${showingTo} of ${filtered.length}`}
          </div>
          <div className="row gap-2 pager-row" style={{ alignItems: 'center' }}>
            <span className="muted fs-12">{isRo ? 'Pe pagină' : 'Per page'}</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                padding: '4px 8px',
                fontSize: 12.5,
                cursor: 'pointer',
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setPage(1)}
              style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
            >
              «
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
            >
              ‹ {isRo ? 'Anterior' : 'Prev'}
            </button>
            <span className="fs-12 pager-count" style={{ minWidth: 80, textAlign: 'center' }}>
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
            >
              {isRo ? 'Următor' : 'Next'} ›
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(totalPages)}
              style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
            >
              »
            </button>
          </div>
        </div>
      )}

      <ContainerViewModal
        isOpen={selected !== null}
        container={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default ContainersIndex;
